import { api, apiErrorMessage } from '@ddone/api-client';
import type { SessionBootstrap } from '@ddone/shared-types';
import { Button, Field, Input, Select } from '@ddone/ui';
import { CheckCircle2, ShieldCheck } from 'lucide-react';
import { type FormEvent, useState } from 'react';

interface InitialSetupScreenProps {
  session: SessionBootstrap;
  onComplete(): Promise<void>;
}

const defaults = {
  name: 'DDone',
  slug: 'ddone',
  timezone: 'Europe/Rome',
  locale: 'it',
  currency: 'EUR'
};

export function InitialSetupScreen({ session, onComplete }: InitialSetupScreenProps) {
  const [form, setForm] = useState(defaults);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>();

  const update = (field: keyof typeof defaults, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError(undefined);
    try {
      await api.request('/setup/bootstrap', { method: 'POST', body: form });
      await onComplete();
    } catch (cause) {
      setError(apiErrorMessage(cause));
    } finally {
      setSubmitting(false);
    }
  };

  if (!session.setupRequired) {
    return (
      <main className="dd-standalone-state">
        <section className="dd-setup-card" aria-labelledby="workspace-access-title">
          <ShieldCheck aria-hidden="true" />
          <h1 id="workspace-access-title">Accesso al workspace necessario</h1>
          <p>
            L’installazione è già configurata, ma il tuo account non appartiene a un workspace
            attivo. Chiedi a un amministratore di aggiungerti.
          </p>
        </section>
      </main>
    );
  }

  if (!session.setupAllowed) {
    return (
      <main className="dd-standalone-state">
        <section className="dd-setup-card" aria-labelledby="setup-role-title">
          <ShieldCheck aria-hidden="true" />
          <h1 id="setup-role-title">Configurazione riservata</h1>
          <p>
            Il primo workspace deve essere creato da una persona con il ruolo di sistema
            <strong> super-admin</strong>. Contatta l’amministratore dell’installazione se il tuo
            account non dispone di questo ruolo.
          </p>
        </section>
      </main>
    );
  }

  return (
    <main className="dd-setup">
      <section className="dd-setup-intro">
        <span className="dd-eyebrow">Prima configurazione</span>
        <h1>Prepariamo il tuo workspace.</h1>
        <p>
          Sei autenticato come {session.user?.displayName}. Questi valori potranno essere modificati
          nella sezione Workspace ERP.
        </p>
        <ul>
          <li>
            <CheckCircle2 aria-hidden="true" /> Accesso amministrativo attivo
          </li>
          <li>
            <CheckCircle2 aria-hidden="true" /> Moduli ERP di base abilitati
          </li>
        </ul>
      </section>
      <form className="dd-setup-card dd-setup-form" onSubmit={(event) => void submit(event)}>
        <h2>Crea il workspace iniziale</h2>
        <p>Inserisci l’identità operativa dell’organizzazione.</p>
        <div className="dd-form-grid">
          <Field label="Nome workspace" htmlFor="setup-name">
            <Input
              id="setup-name"
              required
              maxLength={180}
              value={form.name}
              onChange={(event) => update('name', event.target.value)}
            />
          </Field>
          <Field
            label="Identificativo (slug)"
            htmlFor="setup-slug"
            hint="Nome tecnico stabile composto da minuscole, numeri e trattini."
          >
            <Input
              id="setup-slug"
              required
              maxLength={100}
              pattern="[a-z0-9]+(-[a-z0-9]+)*"
              value={form.slug}
              onChange={(event) => update('slug', event.target.value.toLowerCase())}
            />
          </Field>
          <Field label="Fuso orario" htmlFor="setup-timezone">
            <Input
              id="setup-timezone"
              required
              maxLength={80}
              value={form.timezone}
              onChange={(event) => update('timezone', event.target.value)}
            />
          </Field>
          <Field label="Lingua" htmlFor="setup-locale">
            <Select
              id="setup-locale"
              value={form.locale}
              onChange={(event) => update('locale', event.target.value)}
            >
              <option value="it">Italiano</option>
              <option value="en">English</option>
            </Select>
          </Field>
          <Field label="Valuta" htmlFor="setup-currency">
            <Select
              id="setup-currency"
              value={form.currency}
              onChange={(event) => update('currency', event.target.value)}
            >
              <option value="EUR">EUR — Euro</option>
              <option value="USD">USD — Dollaro statunitense</option>
              <option value="GBP">GBP — Sterlina britannica</option>
              <option value="CHF">CHF — Franco svizzero</option>
            </Select>
          </Field>
        </div>
        {error ? (
          <p className="dd-inline-error" role="alert">
            {error}
          </p>
        ) : null}
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Creazione in corso…' : 'Crea workspace e continua'}
        </Button>
      </form>
    </main>
  );
}
